import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Y from 'yjs';
import { generateHTML, type JSONContent } from '@tiptap/core';
import { schemaExtensions } from '@nexus/editor';
import { useStore, useStoreQuery } from '../db/context';
import { docToJSON } from '../db/content';
import { cn } from '../lib/cn';
import { Dialog } from './Dialog';

/** "Previous versions": pick a snapshot, preview it, restore it as a new edit. */
export function VersionsDialog({
  pageId,
  open,
  onOpenChange,
  onRestore,
}: {
  pageId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore: (doc: JSONContent) => void;
}) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const versions = useStoreQuery((s) => s.listVersions(pageId), [pageId], ['versions']);
  const [selected, setSelected] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<{ id: string; json: JSONContent; html: string } | null>(
    null,
  );
  const preview = loaded && loaded.id === selected ? loaded : null;
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    void store.getVersion(selected).then((state) => {
      if (cancelled || !state) return;
      const doc = new Y.Doc();
      Y.applyUpdate(doc, state);
      const json = docToJSON(doc);
      doc.destroy();
      setLoaded({ id: selected, json, html: generateHTML(json, schemaExtensions()) });
    });
    return () => {
      cancelled = true;
    };
  }, [selected, store]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('versions.title')} wide>
      {!versions?.length ? (
        <p className="text-sm text-fg-muted">{t('versions.none')}</p>
      ) : (
        <div className="grid h-[60vh] grid-cols-[12rem_1fr] gap-4">
          <ul className="overflow-y-auto border-r border-border pr-2 text-sm">
            {versions.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => setSelected(v.id)}
                  aria-pressed={selected === v.id}
                  className={cn(
                    'w-full rounded-md px-2 py-1 text-left hover:bg-bg-hover',
                    selected === v.id && 'bg-bg-active',
                  )}
                >
                  {fmt.format(v.createdAt)}
                </button>
              </li>
            ))}
          </ul>
          <div className="flex min-h-0 flex-col">
            <div
              className="nx-prose min-h-0 flex-1 overflow-y-auto rounded-lg border border-border p-3"
              dangerouslySetInnerHTML={{ __html: preview?.html ?? '' }}
            />
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                disabled={!preview}
                onClick={() => {
                  if (!preview) return;
                  onRestore(preview.json);
                  onOpenChange(false);
                }}
                className="rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg disabled:opacity-40"
              >
                {t('versions.restore')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
